import { useCallback, MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { NavigationItem } from './core/Navigation';
import { useNavigationContext } from './NavigationContext';

export const useReactRouterNavigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const enableUnification = useEnableUnification();
  const {
    setIsSubNavOpen,
    setSelectedPrimaryNavItem,
    closeSubNav,
    isSubNavOpen,
    selectedPrimaryNavItem,
    setIsNavCollapsed,
    initiateExternalRedirect,
  } = useNavigationContext();

  const handlePrimaryItemClick = useCallback(
    (item: NavigationItem, event: MouseEvent) => {
      if (item.disabled) {
        return;
      }

      if (!item.path && !item.externalRedirectKey && !item.subItems?.length) {
        return;
      }

      setSelectedPrimaryNavItem(item);

      if (item.subItems?.length) {
        // prevent navigation for items with subnav
        event.preventDefault();
        // expand nav if collapsed when opening subnav
        setIsNavCollapsed(false);
        // toggle subnav - if the same item is clicked while subnav is open, close it
        if (isSubNavOpen && selectedPrimaryNavItem?.id === item.id) {
          closeSubNav();
        } else {
          setIsSubNavOpen(true);
        }
      } else if (item.externalRedirectKey) {
        // prevent navigation and handle external redirects
        event.preventDefault();
        initiateExternalRedirect(item.externalRedirectKey);
      } else if (item.path) {
        // for internal navigation, let React Router Link handle it
        // just close subnav if open
        setIsSubNavOpen(false);
        // don't prevent default - let Link handle navigation
      }
    },
    [
      setIsSubNavOpen,
      setSelectedPrimaryNavItem,
      closeSubNav,
      isSubNavOpen,
      selectedPrimaryNavItem,
      setIsNavCollapsed,
      initiateExternalRedirect,
    ]
  );

  const handleSubItemClick = useCallback(
    (_subItem: NavigationItem, _parentItem: NavigationItem, _event: MouseEvent) => {
      // close the sub-menu when a sub-item is clicked
      closeSubNav();
      // for subitems with paths, let React Router Link handle navigation
      // don't prevent default - let Link handle navigation
    },
    [closeSubNav]
  );

  const getActiveItemId = useCallback(
    (items: NavigationItem[]): string | undefined => {
      // special handling for markets route since nested in same layout as customers
      // return early pay as the active parent
      if (location.pathname.startsWith('/enterprise/supplier/markets')) {
        return 'early-pay';
      }

      // find the item that matches the current path
      const findActiveItem = (navItems: NavigationItem[]): NavigationItem | undefined => {
        for (const item of navItems) {
          // check if current path starts with item path
          if (item.path && location.pathname.startsWith(item.path)) {
            return item;
          }

          // check subitems - return parent item if subitem matches
          if (item.subItems?.length) {
            for (const subItem of item.subItems) {
              if (subItem.path && location.pathname.startsWith(subItem.path)) {
                return item; // return parent item instead of subItem
              }
            }
          }
        }

        return undefined;
      };

      const activeItem = findActiveItem(items);
      return activeItem?.id;
    },
    [location.pathname]
  );

  const getActiveSubItemId = useCallback(
    (items: NavigationItem[]): string | undefined => {
      // special handling for markets route since nested in same layout as customers
      // return markets as the active subitem
      if (location.pathname.startsWith('/enterprise/supplier/markets')) {
        return 'markets';
      }

      // find the sub-item that matches the current path
      for (const item of items) {
        if (item.subItems?.length) {
          for (const subItem of item.subItems) {
            if (subItem.path && location.pathname.startsWith(subItem.path)) {
              return subItem.id;
            }
          }
        }
      }

      return undefined;
    },
    [location.pathname]
  );

  const getBreadcrumbs = useCallback(
    (items: NavigationItem[]): { primary: NavigationItem | null; sub: NavigationItem | null } => {
      // handle customers route when homepage is enabled
      // show Early Pay / Offer Activity breadcrumb
      if (enableUnification && location.pathname.startsWith('/enterprise/supplier/customers')) {
        const earlyPayItem = items.find((item) => item.id === 'early-pay');

        if (earlyPayItem) {
          const offerActivitySubItem = earlyPayItem.subItems?.find((subItem) => subItem.id === 'offer-activity');

          if (offerActivitySubItem) {
            return {
              primary: earlyPayItem,
              sub: offerActivitySubItem,
            };
          }
        }
      }

      // handle markets route and customers route (when homepage is disabled)
      if (
        location.pathname.startsWith('/enterprise/supplier/markets') ||
        location.pathname.startsWith('/enterprise/supplier/customers')
      ) {
        const earlyPayItem = items.find((item) => item.id === 'early-pay');

        if (earlyPayItem) {
          const marketsSubItem = earlyPayItem.subItems?.find((subItem) => subItem.id === 'markets');

          if (marketsSubItem) {
            return {
              primary: earlyPayItem,
              sub: marketsSubItem,
            };
          }
        }
      }

      // handle account settings routes
      if (location.pathname.startsWith('/enterprise/supplier/account/')) {
        const accountSettingsItem = items.find(
          (item) => item.path === '/enterprise/supplier/account' || item.id === 'account-settings'
        );

        if (accountSettingsItem) {
          const accountRouteMap: Record<string, string> = {
            '/enterprise/supplier/account/settings': t('accountSettings.profile'),
            '/enterprise/supplier/account/notifications': t('accountSettings.notifications'),
            '/enterprise/supplier/account/right-to-be-forgotten': t('accountSettings.rightToBeForgotten'),
          };

          for (const [path, label] of Object.entries(accountRouteMap)) {
            if (location.pathname.startsWith(path)) {
              return {
                primary: accountSettingsItem,
                sub: { ...accountSettingsItem, id: `${accountSettingsItem.id}-${path.split('/').pop()}`, label },
              };
            }
          }

          return { primary: accountSettingsItem, sub: null };
        }
      }

      // handle find your customers routes
      if (location.pathname.startsWith('/enterprise/supplier/find-your-customers/')) {
        const findCustomerItem = items.find(
          (item) => item.path === '/enterprise/supplier/find-your-customers' || item.id === 'find-your-customers'
        );

        if (findCustomerItem) {
          const findCustomerRouteMap: Record<string, string> = {
            '/enterprise/supplier/find-your-customers/expand-your-network': t('findYourCustomers.expandYourNetwork'),
            '/enterprise/supplier/find-your-customers/request-new-customers': t(
              'findYourCustomers.requestNewCustomers'
            ),
          };

          for (const [path, label] of Object.entries(findCustomerRouteMap)) {
            if (location.pathname.startsWith(path)) {
              return {
                primary: findCustomerItem,
                sub: { ...findCustomerItem, id: `${findCustomerItem.id}-${path.split('/').pop()}`, label },
              };
            }
          }

          return { primary: findCustomerItem, sub: null };
        }
      }

      // Handle invoice central routes
      if (location.pathname.startsWith('/enterprise/supplier/invoice-central/')) {
        const invoiceCentralItem = items.find((item) => item.id === 'invoice-central');

        // Invoice Central subitem routes are set to /eligible, but /ineligible and /adjustments for that buyer/currency combination
        // should also show the same breadcrumb path of Invoice Central / <Buyer Name> (<Currency>)
        if (invoiceCentralItem) {
          const currentBuyerCurrency = location.pathname.split('/')[4] + '-' + location.pathname.split('/')[5];
          const invoiceCentralSubItem = invoiceCentralItem.subItems?.find(
            (sub) => sub.id === `invoice-central-${currentBuyerCurrency}`
          );

          if (invoiceCentralSubItem) {
            return { primary: invoiceCentralItem, sub: invoiceCentralSubItem };
          }
        }
      }

      for (const item of items) {
        // direct path match - only primary item
        if (item.path && location.pathname.startsWith(item.path)) {
          return { primary: item, sub: null };
        }

        // check if any subitem matches for breadcrumb chain
        if (item.subItems?.length) {
          for (const subItem of item.subItems) {
            if (subItem.path && location.pathname.startsWith(subItem.path)) {
              return { primary: item, sub: subItem };
            }
          }
        }
      }

      return { primary: null, sub: null };
    },
    [location.pathname, t, enableUnification]
  );

  return {
    handlePrimaryItemClick,
    handleSubItemClick,
    getActiveItemId,
    getActiveSubItemId,
    getBreadcrumbs,
  };
};
